import { useCallback, useMemo } from 'react';
import { EventCategory } from '../EventCategory.model';
import { getEventCategory } from './getEventCategory';

type CheckState = 'some' | 'all' | 'none';

function computeCheckState(
  eventIds: string[],
  selectedSet: Set<string>
): CheckState {
  let count = 0;
  for (const id of eventIds) {
    if (selectedSet.has(id)) count++;
  }
  if (count === 0) return 'none';
  if (count === eventIds.length) return 'all';
  return 'some';
}

export function useEditEventCategory(
  allEventIds: string[],
  selectedEventIds: string[],
  setSelectedEventIds: (eventIds: string[]) => void
): {
  selectedCategoryDic: Record<string, CheckState>;
  selectedSubCategoryDic: Record<string, CheckState>;
  eventCategories: EventCategory[];
  toggleCategory: (categoryId: string) => string[];
  toggleSubCategory: (categoryId: string, subCategoryId: string) => string[];
} {
  const eventCategories = useMemo(
    () => getEventCategory(allEventIds),
    [allEventIds]
  );

  const selectedSet = useMemo(
    () => new Set(selectedEventIds),
    [selectedEventIds]
  );

  const selectedCategoryDic = useMemo(() => {
    const dic: Record<string, CheckState> = {};
    eventCategories.forEach((category) => {
      dic[category.id] = computeCheckState(category.eventIds, selectedSet);
    });
    return dic;
  }, [eventCategories, selectedSet]);

  const selectedSubCategoryDic = useMemo(() => {
    const dic: Record<string, CheckState> = {};
    eventCategories.forEach((category) => {
      category.subCategories.forEach((sub) => {
        const key = `${category.id}.${sub.id}`;
        dic[key] = computeCheckState(sub.eventIds, selectedSet);
      });
    });
    return dic;
  }, [eventCategories, selectedSet]);

  const toggleCategory = useCallback(
    (categoryId: string) => {
      const category = eventCategories.find((cat) => cat.id === categoryId);
      if (!category) return selectedEventIds;

      const areAllSelected = category.eventIds.every((id) =>
        selectedSet.has(id)
      );
      const categoryIdSet = new Set(category.eventIds);

      const newSelectedEventIds = areAllSelected
        ? allEventIds.filter(
            (id) => selectedSet.has(id) && !categoryIdSet.has(id)
          )
        : allEventIds.filter(
            (id) => selectedSet.has(id) || categoryIdSet.has(id)
          );

      setSelectedEventIds(newSelectedEventIds);
      return newSelectedEventIds;
    },
    [
      allEventIds,
      eventCategories,
      selectedSet,
      selectedEventIds,
      setSelectedEventIds,
    ]
  );

  const toggleSubCategory = useCallback(
    (categoryId: string, subCategoryId: string) => {
      const category = eventCategories.find((cat) => cat.id === categoryId);
      const sub = category?.subCategories.find((s) => s.id === subCategoryId);
      if (!sub) return selectedEventIds;

      const areAllSelected = sub.eventIds.every((id) => selectedSet.has(id));
      const subIdSet = new Set(sub.eventIds);

      const newSelectedEventIds = areAllSelected
        ? allEventIds.filter((id) => selectedSet.has(id) && !subIdSet.has(id))
        : allEventIds.filter((id) => selectedSet.has(id) || subIdSet.has(id));

      setSelectedEventIds(newSelectedEventIds);
      return newSelectedEventIds;
    },
    [
      allEventIds,
      eventCategories,
      selectedSet,
      selectedEventIds,
      setSelectedEventIds,
    ]
  );

  return {
    eventCategories,
    toggleCategory,
    toggleSubCategory,
    selectedCategoryDic,
    selectedSubCategoryDic,
  };
}
